/* eslint-disable array-callback-return */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import produce from 'immer'
import { compose } from 'redux'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { find, cloneDeep, isArray, last, difference, isEmpty } from 'lodash'
import 'react-quill/dist/quill.snow.css'
import {
  getFormattedAttrId,
  reIndexResponses,
} from '@edulastic/common/src/helpers'
import loadable from '@loadable/component'
import Progress from '@edulastic/common/src/components/Progress'
import { withNamespaces } from '@edulastic/localization'

import { LANGUAGE_EN } from '@edulastic/constants/const/languages'
import { notification } from '@edulastic/common'
import LanguageContext from '@edulastic/common/src/contexts/LanguageContext'
import { questionType } from '@edulastic/constants'
import { setQuestionDataAction } from '../../../author/QuestionEditor/ducks'
import { updateVariables } from '../../utils/variables'

import { Subtitle } from '../../styled/Subtitle'
import Question from '../../components/Question'
import { getLanguageKeys } from '../../../common/utils/helpers'
import {
  getCurrentLanguage,
  getLanguageTabsSelector,
} from '../../../common/components/LanguageSelectorTab/duck'
import ClearContentConfirmationModal from '../../components/ConfirmClearStimulusModal'
import { getCurrentQuestionSelector } from '../../../author/sharedDucks/questions'

import { initializeLanguageObj } from '../../utils/question'
import { INITIAL_FIELD_VALUES_BY_QTYPE } from '../../constants/constantsForQuestions'

const INITIAL_FIELD_VALUES =
  INITIAL_FIELD_VALUES_BY_QTYPE[questionType.CLOZE_TEXT]

const FroalaEditor = loadable(() =>
  import(
    /* webpackChunkName: "froalaCommonChunk" */ '@edulastic/common/src/components/FroalaEditor'
  )
)

class TemplateMarkup extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    item: PropTypes.object.isRequired,
    setQuestionData: PropTypes.func.isRequired,
    fillSections: PropTypes.func,
    cleanSections: PropTypes.func,
  }

  static defaultProps = {
    fillSections: () => {},
    cleanSections: () => {},
  }

  constructor(props) {
    super(props)
    this.state = {
      showClearContentModal: false,
      prevItem: {},
    }
  }

  componentWillUnmount() {
    this.setState({
      showClearContentModal: undefined,
      prevItem: undefined,
    })
  }

  toggleShowClearContentModal = () => {
    this.setState((prevState) => ({
      ...prevState,
      showClearContentModal: !prevState.showClearContentModal,
    }))
  }

  setTempContent = (prevItem = {}) => {
    this.setState((prevState) => ({
      ...prevState,
      prevItem: cloneDeep(prevItem),
    }))
  }

  handleModalResponse = (response) => {
    const { setQuestionData, item } = this.props
    const { prevItem } = this.state
    if (response === 'clear') {
      // Reset the stimulus and options
      setQuestionData(
        produce(item, (draft) => {
          draft.stimulus = ''
          draft.responseIds = []
          draft.validation = {
            validResponse: {
              value: [],
            },
          }
          getLanguageKeys(draft).forEach((lang) => {
            draft.languageFeatures[lang].stimulus = ''
            draft.languageFeatures[lang].validation = {
              validResponse: {
                value: [],
              },
            }
          })
          updateVariables(draft)
        }),
        LANGUAGE_EN
      )
      this.toggleShowClearContentModal()
      this.setTempContent()
    } else {
      if (!isEmpty(prevItem)) {
        setQuestionData(prevItem, LANGUAGE_EN)
      }
      this.toggleShowClearContentModal()
      this.setTempContent()
    }
  }

  onChangeMarkUp = (stimulus) => {
    try {
      const {
        item,
        setQuestionData,
        languageTabs,
        currentLanguage,
        questionData,
      } = this.props
      const { stimulus: prevStimulus = '' } = item
      const reduceResponseIds = (tmpl) => {
        const newResponseIds = []
        if (!window.$) {
          return newResponseIds
        }
        const temp = tmpl || ''
        // eslint-disable-next-line no-undef
        const parsedHTML = $('<div />').html(temp)
        // eslint-disable-next-line no-undef
        $(parsedHTML)
          .find('textinput')
          // eslint-disable-next-line func-names
          .each(function (index) {
            // eslint-disable-next-line no-undef
            const id = $(this).attr('id')
            newResponseIds.push({ index, id })
          })

        return newResponseIds
      }

      const reduceValidations = (responseIds, validation) => {
        const _validation = cloneDeep(validation)
        const _responseIds = cloneDeep(responseIds)
        const validResponses = _validation.validResponse.value

        // remove deleted dropdown answer
        validResponses.map((answer, i) => {
          const { id } = answer
          if (!id) {
            validResponses.splice(i, 1)
          }
          const isExist = find(_responseIds, (response) => response.id === id)
          if (!isExist) {
            validResponses.splice(i, 1)
          }
        })

        // add new correct answers with response id
        _responseIds.map((response) => {
          const { id } = response
          const valid = find(validResponses, (answer) => answer.id === id)
          if (!valid) {
            validResponses.push({ id, value: '', index: response.index })
          } else {
            valid.index = response.index
          }
        })
        validResponses.sort((a, b) => a.index - b.index)
        _validation.validResponse.value = validResponses

        // reduce alternate answers
        if (isArray(_validation.altResponses)) {
          _validation.altResponses.map((altAnswers) => {
            if (
              _validation.validResponse.value.length > altAnswers.value.length
            ) {
              altAnswers.value.push(last(_validation.validResponse.value))
            }
            altAnswers.value.map((altAnswer, index) => {
              const isExist = find(
                _responseIds,
                (response) => response.id === altAnswer.id
              )
              if (!isExist) {
                altAnswers.value.splice(index, 1)
              }
            })
            altAnswers.value.sort((a, b) => a.index - b.index)
          })
        }

        return _validation
      }
      /**
       * getLanguageKeys is a helper function to get the language keys of the current item
       */
      const languageKeys = getLanguageKeys(item)

      /**
       * when stimulus is empty we need to set the primary language as the first language
       * when stimulus is not empty we need to set the primary language as the English
       */
      const [primaryLanguage] = languageTabs

      /**
       * responseIds is the array of response box ids in the current stimulus
       */
      const responseIds = reduceResponseIds(stimulus) || []

      /**
       * prevResponseIds is the array of response box ids in the previous stimulus
       */
      const prevResponseIds = reduceResponseIds(prevStimulus) || []
      /**
       * differenceOfResponseBoxCount is the difference of response box count between the previous stimulus and the current stimulus
       */
      const differenceOfResponseBoxCount =
        prevResponseIds?.length - responseIds?.length

      /**
       * isNewStimulusEmpty is a flag to check if the new stimulus is empty and the previous stimulus is not empty
       * and the difference of response box count is not 0
       */
      const isNewStimulusEmpty =
        differenceOfResponseBoxCount !== 0 &&
        (prevStimulus || '').trim().length &&
        !(stimulus || '').trim().length
      /**
       * when currentLanguage is not the primary language and the difference of response box count is not 0
       * we need to show the clear content modal
       */
      if (
        currentLanguage !== primaryLanguage &&
        differenceOfResponseBoxCount !== 0
      ) {
        /**
         * When stimulus is empty user is trying to remove all content + response boxes
         */
        if (isNewStimulusEmpty) {
          this.toggleShowClearContentModal(true)
          this.setTempContent(questionData)
          /** empty the stimulus for state update, require to update the component */
          setQuestionData(
            produce(questionData, (draft) => {
              draft.stimulus = ''

              languageKeys.forEach((lang) => {
                if (draft.languageFeatures[lang]) {
                  draft.languageFeatures[lang].stimulus = ''
                }
              })
            }),
            LANGUAGE_EN
          )
          return
        }
        /**
         * Here we return because response boxes are changed but stimulus is still present
         * It means user is trying to remove the response in other language tab (not primary),
         * No change FroalaEditor is reverting response box changes
         */
        notification({
          type: 'warn',
          messageKey: 'warnChangeInResponseBoxes',
        })
        return
      }

      /** Removes response boxes */
      if (differenceOfResponseBoxCount > 0) {
        const removedResponseIds = difference(
          prevResponseIds.map((response) => response.id),
          responseIds.map((response) => response.id)
        )
        setQuestionData(
          produce(questionData, (draft) => {
            if (primaryLanguage === LANGUAGE_EN) {
              draft.stimulus = stimulus
              draft.responseIds = responseIds
              draft.validation = reduceValidations(
                draft.responseIds,
                draft.validation
              )
            } else {
              initializeLanguageObj(
                draft,
                primaryLanguage,
                INITIAL_FIELD_VALUES
              )

              draft.languageFeatures[primaryLanguage].stimulus = stimulus
              draft.responseIds = reduceResponseIds(stimulus)

              draft.languageFeatures[
                primaryLanguage
              ].validation = reduceValidations(
                draft.responseIds,
                draft.languageFeatures[primaryLanguage].validation
              )
            }
            languageKeys
              .filter((language) => language !== primaryLanguage)
              .forEach((lang) => {
                initializeLanguageObj(draft, lang, INITIAL_FIELD_VALUES)

                const languageStimulus =
                  draft.languageFeatures[lang].stimulus || ''
                const parsedHTML = $('<div />').html(languageStimulus)
                if (languageStimulus) {
                  removedResponseIds.forEach((responseId) => {
                    parsedHTML.find(`textinput[key="${responseId}"]`).remove()
                  })
                }
                draft.languageFeatures[lang].stimulus = reIndexResponses(
                  parsedHTML.html() || ''
                )

                draft.languageFeatures[lang].validation = reduceValidations(
                  draft.responseIds,
                  draft.languageFeatures[lang].validation
                )
              })

            updateVariables(draft)
          }),
          LANGUAGE_EN
        )
        return
      }

      /** 2. Adds response boxes */
      if (differenceOfResponseBoxCount < 0) {
        const newResponses = difference(
          responseIds.map((response) => response.id),
          prevResponseIds.map((response) => response.id)
        )

        setQuestionData(
          produce(questionData, (draft) => {
            if (primaryLanguage === LANGUAGE_EN) {
              draft.stimulus = stimulus
              draft.responseIds = reduceResponseIds(stimulus)
              draft.validation = reduceValidations(
                draft.responseIds,
                draft.validation
              )
            } else {
              initializeLanguageObj(
                draft,
                primaryLanguage,
                INITIAL_FIELD_VALUES
              )
              draft.languageFeatures[primaryLanguage].stimulus = stimulus
              draft.responseIds = responseIds
              draft.languageFeatures[
                primaryLanguage
              ].validation = reduceValidations(
                draft.responseIds,
                draft.languageFeatures[primaryLanguage].validation
              )
            }
            languageKeys
              .filter((language) => language !== primaryLanguage)
              .forEach((lang) => {
                initializeLanguageObj(draft, lang, INITIAL_FIELD_VALUES)

                let languageStimulus =
                  draft.languageFeatures[lang].stimulus || ''
                let currentIndex = responseIds.length
                newResponses.forEach((responseId) => {
                  languageStimulus += `&nbsp;<textinput id="${responseId}" key="${responseId}" responseindex=${currentIndex}></textinput>&nbsp;`
                  currentIndex++
                })
                draft.languageFeatures[lang].stimulus = languageStimulus

                draft.languageFeatures[lang].validation = reduceValidations(
                  draft.responseIds,
                  draft.languageFeatures[lang].validation
                )
              })
            updateVariables(draft)
          }),
          LANGUAGE_EN
        )
        return
      }

      // 3. No change in response boxes
      setQuestionData(
        produce(item, (draft) => {
          draft.stimulus = stimulus
          draft.responseIds = responseIds
          draft.validation = reduceValidations(
            draft.responseIds,
            draft.validation
          )

          updateVariables(draft)
        })
      )
    } catch (error) {
      console.log('Error in onChangeMarkUp:', error)
    }
  }

  render() {
    const {
      t,
      item,
      fillSections,
      cleanSections,
      froalaFooter,
      currentLanguage,
      languageTabs = [],
    } = this.props
    const { showClearContentModal } = this.state

    return (
      <Question
        section="main"
        label={t('component.cloze.text.composequestion')}
        fillSections={fillSections}
        cleanSections={cleanSections}
      >
        <Subtitle
          id={getFormattedAttrId(
            `${item?.title}-${t('component.cloze.text.composequestion')}`
          )}
        >
          {t('component.cloze.text.composequestion')}
        </Subtitle>

        <LanguageContext.Provider value={{ currentLanguage, languageTabs }}>
          <FroalaEditor
            footer={froalaFooter}
            fallback={<Progress />}
            data-cy="templateBox"
            onChange={this.onChangeMarkUp}
            value={item.stimulus}
            toolbarId="cloze-text-template"
            additionalToolbarOptions={['textinput']}
            border="border"
          />
        </LanguageContext.Provider>
        <ClearContentConfirmationModal
          handleModalResponse={this.handleModalResponse}
          showClearContentModal={showClearContentModal}
          primaryLanguage={languageTabs[0]}
        />
      </Question>
    )
  }
}

const enhance = compose(
  withRouter,
  withNamespaces('assessment'),
  connect(
    (state) => ({
      currentLanguage: getCurrentLanguage(state),
      languageTabs: getLanguageTabsSelector(state),
      questionData: getCurrentQuestionSelector(state),
    }),
    { setQuestionData: setQuestionDataAction }
  )
)

export default enhance(TemplateMarkup)
